package at.apa.statistictracker.service

import at.apa.statistictracker.configuration.Configuration
import at.apa.statistictracker.data.local.StatisticPayloadDao
import at.apa.statistictracker.data.local.UserPropertyDao
import at.apa.statistictracker.data.model.StatisticPayload
import at.apa.statistictracker.data.model.statisticevent.StatisticEvent
import at.apa.statistictracker.data.model.userproperty.UserProperty
import at.apa.statistictracker.data.remote.StatisticTrackerApiException
import at.apa.statistictracker.data.remote.StatisticTrackerApiService
import at.apa.statistictracker.logger.Logger
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.datetime.Clock

internal class StatisticEventService(
    private val configuration: Configuration,
    private val statisticPayloadDao: StatisticPayloadDao,
    private val userPropertyDao: UserPropertyDao,
    private val statisticTrackerApiService: StatisticTrackerApiService
) {

    private val dbLock = Mutex()

    /**
     * Saves a [StatisticEvent] serialized and wrapped in a [StatisticPayload] to the local database.
     *
     * @param statisticEvent the statistic event to be saved.
     */
    suspend fun trackStatisticEventLocally(statisticEvent: StatisticEvent) = coroutineScope {
        Logger.d(TAG, "trackStatisticEventLocally() with statistic event $statisticEvent")
        statisticPayloadDao.insert(appendUserPropertiesToCopy(statisticEvent).toStatisticPayload())
    }

    /**
     * Dispatches the local events to the backend.
     *
     * This method reads all local database payloads and sends them to the backend in bundles.
     * The bundle size is defined by the [configuration] via [Configuration.dispatchBundleSize].
     * After successfully sending the bundle to the backend, the statistic event payloads get removed from the database.
     */
    suspend fun dispatchStatisticEventsToBackend() = coroutineScope {
        Logger.d(TAG, "dispatchStatisticEventsToBackend()")

        val dispatchBundleSize = configuration.dispatchBundleSize

        // Lock database because concurrent calls to this method could fetch unprocessed/undeleted payloads from the database.
        // Therefore wait until all loaded (getAllStatisticPayloads) payloads are sent to the backend and deleted from the database.
        dbLock.withLock {
            val payloads = statisticPayloadDao.getAllStatisticPayloads()

            if (payloads.isNotEmpty()){
                Logger.d(TAG, "dispatchStatisticEventsToBackend() - All payloads to be sent: $payloads")
            }

            payloads.chunked(dispatchBundleSize).forEach { statisticPayloads ->
                Logger.d(
                    TAG,
                    "dispatchStatisticEventsToBackend() - Sending payload chunk: $statisticPayloads"
                )

                try {
                    statisticTrackerApiService.postEvents(statisticPayloads.map { it.payload })
                    statisticPayloadDao.deleteIds(*statisticPayloads.map { it.id }.toLongArray())

                } catch (e: StatisticTrackerApiException) {
                    Logger.e(TAG, "Dispatching payload chunk failed.", e)

                    if (e is StatisticTrackerApiException.ValidationError) {
                        Logger.d(TAG, "ValidationError, deleting payload batch")
                        // At least one of the sent events cannot be processed by the backend
                        // If validation fails now, validation will also fail for future attempts.
                        // Therefore delete the whole batch (successfully sent + failed because of validation) from the local DB
                        statisticPayloadDao.deleteIds(*statisticPayloads.map { it.id }.toLongArray())
                    }

                } catch (e: Exception) {
                    Logger.e(TAG, "Failed with other error", e)
                }
            }
        }
    }

    /**
     * Tries to send a [statisticEvent] directly to the backend.
     *
     * Note that in the event of an error, the event is discarded.
     *
     * @param statisticEvent the [statisticEvent] to be sent.
     */
    suspend fun sendStatisticEvent(statisticEvent: StatisticEvent) = coroutineScope {
        Logger.d(TAG, "sendStatisticEvent() with statistic event $statisticEvent")

        try {
            statisticTrackerApiService.postEvent(appendUserPropertiesToCopy(statisticEvent).toStatisticPayload().payload)
        } catch (e: StatisticTrackerApiException) {
            Logger.e(TAG, "Sending event failed.", e)
        } catch (t: Throwable) {
            Logger.e(TAG, "Failed with other error", t)
            if (t is CancellationException) {
                throw t
            } else Unit
        }
    }

    /**
     * Sets a [UserProperty].
     *
     * @param userProperty the user property to be set.
     */
    suspend fun setUserProperty(userProperty: UserProperty) {
        Logger.d(TAG, "setUserProperty() with user property $userProperty")

        userPropertyDao.insertOrReplace(userProperty)
    }

    private fun StatisticEvent.toStatisticPayload() = StatisticPayload(
        this.serializeWithCustomSerializer(),
        Clock.System.now().toEpochMilliseconds()
    ).also {
        Logger.d(
            TAG,
            "toStatisticPayload() converted statistic event $this to statistic payload $it"
        )
    }

    private suspend fun appendUserPropertiesToCopy(statisticEvent: StatisticEvent): StatisticEvent {
        val userProperties = userPropertyDao.getAllUserProperties()
        Logger.d(TAG, "appendUserProperties() fetched user properties: $userProperties")

        // apply user properties to copy of the passed event, otherwise native targets crash
        // because of mutation attempt of frozen objects
        val statisticEventCopy = statisticEvent.createCopy()

        userProperties.forEach {
            it.applyToEvent(statisticEventCopy)
        }

        return statisticEventCopy
    }

    companion object {
        private const val TAG = "StatisticTrackerService"
    }

}
